// ============================================================
// 14. ShiftSummaryReportPage.tsx
// ============================================================
// src/features/reports/pages/ShiftSummaryReportPage.tsx

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { getBranches } from '@/features/settings/services/settingsApi';
import { getShiftSummaryReport, getExportUrl } from '../services/reportsApi';
import { ReportHeader } from '../components/ReportHeader';
import { ReportFilters } from '../components/ReportFilters';
import { SummaryCard } from '../components/SummaryCard';
import {
    Clock, DollarSign, ShoppingCart, RotateCcw,
    UserCheck, AlertTriangle, CheckCircle2, XCircle,
    Timer, Wallet, CreditCard, Smartphone, Building2,
    Banknote
} from 'lucide-react';
import { motion } from 'framer-motion';

export function ShiftSummaryReportPage() {
    const user = useAuthStore((s) => s.user);
    const tenantId = user?.tenantId || '';

    const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
    const [branchId, setBranchId] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [expandedShift, setExpandedShift] = useState<string | null>(null);

    const { data: branches } = useQuery({
        queryKey: ['branches-report', tenantId],
        queryFn: () => getBranches(tenantId),
        enabled: !!tenantId,
    });

    const params = {
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        branchId: branchId || undefined,
    };

    const { data, isLoading } = useQuery({
        queryKey: ['report-shift-summary', tenantId, params],
        queryFn: () => getShiftSummaryReport(tenantId, params),
        enabled: !!tenantId,
    });

    const formatNaira = (val: string | number) =>
        `₦${parseFloat(String(val || '0')).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
    const formatNumber = (val: string | number) => parseFloat(String(val || '0')).toLocaleString();

    const branchesList = (branches || []) as any[];

    const setQuickDate = (days: number) => {
        const end = new Date();
        const start = new Date();
        if (days === 0) start.setHours(0, 0, 0, 0);
        else start.setDate(start.getDate() - days);
        setDateRange({ startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] });
    };

    const handleExport = (format: 'pdf' | 'excel') => {
        const url = getExportUrl(tenantId, 'shift_summary', format, params as any);
        window.open(url, '_blank');
    };

    const hasFilters = !!(dateRange.startDate || dateRange.endDate || branchId);
    const clearFilters = () => { setDateRange({ startDate: '', endDate: '' }); setBranchId(''); };

    const shifts = data?.shifts || [];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-cyan-50/30">
            <ReportHeader
                title="Shift Summary"
                description="Cashier shift reconciliation"
                icon={Clock}
                iconGradient="from-cyan-500 to-blue-600"
                hasFilters={hasFilters}
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters(!showFilters)}
                onExportPdf={() => handleExport('pdf')}
                onExportExcel={() => handleExport('excel')}
                branchName={branchesList.find(b => b.id === branchId)?.name}
            />

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-5">
                <ReportFilters
                    visible={showFilters}
                    dateRange={dateRange}
                    branchId={branchId}
                    branches={branchesList}
                    onDateRangeChange={setDateRange}
                    onBranchChange={setBranchId}
                    onClear={clearFilters}
                    onQuickDate={setQuickDate}
                />

                {isLoading ? (
                    <div className="flex justify-center py-20"><Spinner size="xl" /></div>
                ) : !data || shifts.length === 0 ? (
                    <EmptyState
                        icon={<Clock className="w-10 h-10" />}
                        title="No Shift Data"
                        description="No shift records found for the selected period."
                        size="lg"
                    />
                ) : (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                            <SummaryCard
                                label="Total Shifts"
                                value={String(data.summary?.totalShifts || 0)}
                                icon={Timer}
                                gradient="from-cyan-500 to-blue-600"
                            />
                            <SummaryCard
                                label="Open Shifts"
                                value={String(data.summary?.openShifts || 0)}
                                icon={AlertTriangle}
                                gradient="from-amber-500 to-orange-600"
                                subtitle="Needs attention"
                            />
                            <SummaryCard
                                label="Total Sales"
                                value={formatNaira(data.summary?.totalSales || 0)}
                                icon={DollarSign}
                                gradient="from-emerald-500 to-teal-600"
                            />
                            <SummaryCard
                                label="Cash Variance"
                                value={formatNaira(data.summary?.totalCashVariance || 0)}
                                icon={Wallet}
                                gradient={parseFloat(data.summary?.totalCashVariance || '0') === 0
                                    ? 'from-emerald-500 to-teal-600'
                                    : 'from-rose-500 to-pink-600'
                                }
                            />
                            <SummaryCard
                                label="Transactions"
                                value={formatNumber(data.summary?.totalTransactions || 0)}
                                icon={ShoppingCart}
                                gradient="from-purple-500 to-violet-600"
                            />
                        </div>

                        {/* Open Shifts Warning */}
                        {data.summary?.openShifts > 0 && (
                            <Card className="border-amber-300 bg-amber-50/50">
                                <CardContent className="p-4 flex items-center gap-3">
                                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                                    <div>
                                        <p className="text-sm font-bold text-amber-800">
                                            {data.summary.openShifts} shift{data.summary.openShifts > 1 ? 's' : ''} still open
                                        </p>
                                        <p className="text-xs text-amber-600">
                                            Open shifts should be closed for accurate reconciliation
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Shifts List */}
                        <div className="space-y-3">
                            {shifts.map((shift: any, i: number) => {
                                const isExpanded = expandedShift === shift.id;
                                const isOpen = shift.status === 'open';

                                return (
                                    <motion.div
                                        key={i}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: i * 0.02 }}
                                    >
                                        <Card
                                            hover
                                            className={`cursor-pointer border-l-4 ${isOpen ? 'border-l-amber-500' :
                                                    shift.varianceStatus === 'balanced' ? 'border-l-emerald-500' :
                                                        shift.varianceStatus === 'over' ? 'border-l-blue-500' :
                                                            'border-l-rose-500'
                                                }`}
                                            onClick={() => setExpandedShift(isExpanded ? null : shift.id)}
                                        >
                                            <CardContent className="p-4">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-4">
                                                        <div className={`p-2 rounded-xl ${isOpen ? 'bg-amber-100' : 'bg-emerald-100'
                                                            }`}>
                                                            <Clock className={`w-5 h-5 ${isOpen ? 'text-amber-600' : 'text-emerald-600'
                                                                }`} />
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-bold text-slate-900">{shift.userName}</p>
                                                            <div className="flex items-center gap-2 text-xs text-slate-500">
                                                                <span>{shift.branchName}</span>
                                                                <span>•</span>
                                                                <span>{new Date(shift.openedAt).toLocaleDateString()}</span>
                                                                <span>•</span>
                                                                <span>
                                                                    {new Date(shift.openedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                    {shift.closedAt ? ` - ${new Date(shift.closedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ' - Ongoing'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-4">
                                                        <div className="text-right">
                                                            <p className="text-lg font-bold text-slate-900">{formatNaira(shift.totalSales)}</p>
                                                            <p className="text-xs text-slate-400">{shift.transactionCount} transactions</p>
                                                        </div>
                                                        <Badge
                                                            variant={isOpen ? 'warning' : shift.varianceStatus === 'balanced' ? 'success' : 'danger'}
                                                            size="sm"
                                                            pulse={isOpen}
                                                        >
                                                            {isOpen ? 'OPEN' : shift.varianceStatus?.toUpperCase() || 'CLOSED'}
                                                        </Badge>
                                                    </div>
                                                </div>

                                                {/* Expanded Details */}
                                                {isExpanded && (
                                                    <motion.div
                                                        initial={{ opacity: 0, height: 0 }}
                                                        animate={{ opacity: 1, height: 'auto' }}
                                                        className="mt-4 pt-4 border-t border-slate-100"
                                                    >
                                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                                            <div className="bg-slate-50 rounded-xl p-3">
                                                                <p className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                                                                    <Wallet size={12} /> Opening Cash
                                                                </p>
                                                                <p className="text-sm font-bold">{formatNaira(shift.openingCash)}</p>
                                                            </div>
                                                            <div className="bg-slate-50 rounded-xl p-3">
                                                                <p className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                                                                    <DollarSign size={12} /> Expected Cash
                                                                </p>
                                                                <p className="text-sm font-bold">{formatNaira(shift.expectedCash)}</p>
                                                            </div>
                                                            <div className="bg-slate-50 rounded-xl p-3">
                                                                <p className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                                                                    <CreditCard size={12} /> Counted Cash
                                                                </p>
                                                                <p className="text-sm font-bold">{formatNaira(shift.countedCash || '0')}</p>
                                                            </div>
                                                            <div className={`rounded-xl p-3 ${!shift.cashVariance || parseFloat(shift.cashVariance) === 0
                                                                    ? 'bg-emerald-50'
                                                                    : 'bg-rose-50'
                                                                }`}>
                                                                <p className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                                                                    <AlertTriangle size={12} /> Variance
                                                                </p>
                                                                <p className={`text-sm font-bold ${!shift.cashVariance || parseFloat(shift.cashVariance) === 0
                                                                        ? 'text-emerald-600'
                                                                        : 'text-rose-600'
                                                                    }`}>
                                                                    {formatNaira(shift.cashVariance || '0')}
                                                                </p>
                                                            </div>
                                                        </div>

                                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                                            <div className="flex items-center gap-2">
                                                                <Banknote className="w-4 h-4 text-emerald-500" />
                                                                <div>
                                                                    <p className="text-xs text-slate-500">Cash Sales</p>
                                                                    <p className="text-sm font-semibold">{formatNaira(shift.totalCashSales)}</p>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <CreditCard className="w-4 h-4 text-blue-500" />
                                                                <div>
                                                                    <p className="text-xs text-slate-500">Card Sales</p>
                                                                    <p className="text-sm font-semibold">{formatNaira(shift.totalCardSales)}</p>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <Building2 className="w-4 h-4 text-purple-500" />
                                                                <div>
                                                                    <p className="text-xs text-slate-500">Transfer Sales</p>
                                                                    <p className="text-sm font-semibold">{formatNaira(shift.totalTransferSales)}</p>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <RotateCcw className="w-4 h-4 text-rose-500" />
                                                                <div>
                                                                    <p className="text-xs text-slate-500">Voids/Refunds</p>
                                                                    <p className="text-sm font-semibold">{shift.voidCount}/{shift.refundCount}</p>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {shift.duration && (
                                                            <p className="text-xs text-slate-400 mt-3">
                                                                Shift duration: {shift.duration} hours
                                                            </p>
                                                        )}
                                                    </motion.div>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}